import { ArrowRight, Heart, Crown, Sparkles, HandHeart, Compass, Wind } from 'lucide-react';

const GoDeeper = () => {
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>

      {/* ===== SECTION 1: HERO WITH IMAGE ===== */}
      <section className="relative min-h-[85vh] flex items-center" style={{ backgroundColor: '#1A3C34' }}>
        {/* Background image overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1649131023588-97f888bd56ff?w=1400&q=80"
            alt="Woman in reflection"
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,60,52,0.4) 0%, rgba(26,60,52,0.85) 100%)' }} />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10 py-20">
          <p className="text-[11px] uppercase tracking-[6px] mb-10" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
            Sister Diamonds
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.2] mb-6" data-testid="hero-headline">
            You're Not Here<br />by Accident
          </h1>
          <div className="w-16 h-[1px] mx-auto mb-8" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-base lg:text-lg text-white/60 max-w-lg mx-auto leading-relaxed mb-4" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            At some point, seeing the pattern is not enough.
          </p>
          <p className="text-base lg:text-lg text-white/60 max-w-lg mx-auto leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            You have to decide what you're going to do with it.
          </p>
          {/* Early CTA */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <a
              href="/inner-circle"
              className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide"
              style={{ backgroundColor: '#C06C54' }}
              data-testid="hero-cta-inner-circle"
            >
              Join Inner Circle <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="https://calendly.com/crystal-and-dr-nicole/one-on-one-with-crystal-and-dr-nicole"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide"
              style={{ backgroundColor: '#1A3C34' }}
              data-testid="hero-cta-collective"
            >
              Let's Talk
            </a>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: MIRROR WITH IMAGE ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1528874077309-9e15912d86e9?w=800&q=80"
                  alt="Woman in contemplation"
                  className="w-full h-[420px] object-cover"
                  data-testid="mirror-image"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-20" style={{ backgroundColor: '#C06C54' }} />
            </div>
            {/* Text */}
            <div className="order-1 lg:order-2 space-y-6">
              <p className="text-3xl sm:text-4xl font-light leading-snug" style={{ color: '#1A3C34' }}>
                You've seen it.
              </p>
              <p className="text-3xl sm:text-4xl font-light leading-snug" style={{ color: '#1A3C34' }}>
                You've felt it.
              </p>
              <div className="w-10 h-[1px]" style={{ backgroundColor: '#C06C54' }} />
              <p className="text-base leading-[1.8]" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                You've recognized that something in your life feels off &mdash; that you're carrying more than you should &mdash; that the way you've been operating is no longer sustainable.
              </p>
              <p className="text-xl sm:text-2xl font-medium pt-2" style={{ color: '#1A3C34' }}>
                And now you're at a moment most women avoid.
              </p>
              <p className="text-2xl sm:text-3xl font-semibold" style={{ color: '#C06C54' }}>
                The decision.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ===== SECTION 4: TWO CLEAR PATHS / CTA CARDS (MOVED UP) ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[11px] uppercase tracking-[5px] mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
              Choose Your Path
            </p>
            <h2 className="text-3xl sm:text-4xl font-light" style={{ color: '#1A3C34' }}>
              Two Ways Forward
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {/* OPTION 1: Inner Circle */}
            <div
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 group"
              style={{ border: '1px solid rgba(26,60,52,0.06)' }}
              data-testid="path-inner-circle"
            >
              {/* Card Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1611432580340-af48bd7549ed?w=800&q=80"
                  alt="Sisters in connection"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 lg:p-9 flex flex-col">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: '#C06C54' }}>
                  <Heart className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-semibold mb-2" style={{ color: '#1A3C34' }}>
                  I need a space to keep going
                </h3>
                <p className="text-[11px] uppercase tracking-[3px] mb-5" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                  Sister Diamonds Inner Circle
                </p>
                <div className="w-10 h-[1px] mb-5" style={{ backgroundColor: '#E8DCCA' }} />
                <div className="mb-7 space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#1A3C34', fontWeight: 300 }}>You want:</p>
                  <ul className="space-y-2.5">
                    {[
                      'Ongoing support',
                      'Reflection & soul nourishment',
                      'Community of women who see you',
                      'Tools to help you stay grounded'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(26,60,52,0.7)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                        <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: '#C06C54' }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto">
                  <a
                    href="/inner-circle"
                    className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full w-full justify-center transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-md text-sm tracking-wide"
                    style={{ backgroundColor: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
                    data-testid="join-inner-circle-btn"
                  >
                    Join the Inner Circle <ArrowRight className="h-4 w-4" />
                  </a>
                  <p className="text-center text-xs mt-3 italic" style={{ color: 'rgba(26,60,52,0.45)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                    No pressure. Just a place to continue.
                  </p>
                </div>
              </div>
            </div>

            {/* OPTION 2: Collective */}
            <div
              className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 group"
              style={{ backgroundColor: '#1A3C34' }}
              data-testid="path-collective"
            >
              {/* Card Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/ygu1p8r9_4%20%281%29.png"
                  alt="Woman at peace"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70"
                />
              </div>
              <div className="p-8 lg:p-9 flex flex-col">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: '#C06C54' }}>
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2">
                  I need space to reset
                </h3>
                <p className="text-[11px] uppercase tracking-[3px] mb-5" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                  Sister Diamonds Collective
                </p>
                <div className="w-10 h-[1px] mb-5" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                <div className="mb-7 space-y-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <p className="text-sm leading-relaxed text-white/70" style={{ fontWeight: 300 }}>You know:</p>
                  <ul className="space-y-2.5">
                    {[
                      'This is deeper than conversation',
                      'You need to step out of your environment',
                      "You're ready to shift something in a real way",
                      'You want direct partnership with Crystal & Dr. Nicole'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-white/60" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                        <Crown className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: '#C06C54' }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto">
                  <a
                    href="https://calendly.com/crystal-and-dr-nicole/one-on-one-with-crystal-and-dr-nicole"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full w-full justify-center transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-md text-sm tracking-wide"
                    style={{ backgroundColor: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
                    data-testid="apply-collective-btn"
                  >
                    Let's Talk <ArrowRight className="h-4 w-4" />
                  </a>
                  <p className="text-center text-xs mt-3 italic text-white/35" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                    Apply &mdash; we'll determine together if it's the right next step.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: TWO PATHS NARRATIVE ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-base lg:text-lg mb-16" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            Right now, there are two paths in front of you.
          </p>
          <div className="grid md:grid-cols-2 gap-10 lg:gap-20">
            {/* Path 1: Stay */}
            <div className="text-left">
              <div className="rounded-2xl overflow-hidden shadow-md mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/5suqpaxf_thumb%208.jpeg"
                  alt="Woman in stillness"
                  className="w-full h-56 object-cover"
                  data-testid="path-stay-image"
                />
              </div>
              <p className="text-[11px] uppercase tracking-[4px] mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                Path One
              </p>
              <h3 className="text-2xl sm:text-3xl font-light mb-6" style={{ color: '#1A3C34' }}>
                Stay where you are
              </h3>
              <div className="space-y-3" style={{ fontFamily: "'Manrope', sans-serif", color: '#1A3C34', fontWeight: 300 }}>
                <p className="text-base leading-[1.8] opacity-70">Keep managing it.</p>
                <p className="text-base leading-[1.8] opacity-70">Keep pushing through.</p>
                <p className="text-base leading-[1.8] opacity-70">Keep carrying it the way you have been.</p>
                <div className="pt-4">
                  <p className="text-base leading-[1.8] opacity-50">Nothing breaks.</p>
                  <p className="text-base leading-[1.8] opacity-50 italic">But nothing really shifts either.</p>
                </div>
              </div>
            </div>
            {/* Path 2: Change */}
            <div className="text-left relative">
              <div className="rounded-2xl overflow-hidden shadow-md mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/ho16mpku_9.png"
                  alt="Sisters standing together"
                  className="w-full h-56 object-cover"
                  data-testid="path-change-image"
                />
              </div>
              <p className="text-[11px] uppercase tracking-[4px] mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                Path Two
              </p>
              <h3 className="text-2xl sm:text-3xl font-light mb-6" style={{ color: '#1A3C34' }}>
                Do something different
              </h3>
              <div className="space-y-3" style={{ fontFamily: "'Manrope', sans-serif", color: '#1A3C34', fontWeight: 300 }}>
                <p className="text-base leading-[1.8] opacity-70">Not everything.</p>
                <p className="text-base leading-[1.8] opacity-70">Not all at once.</p>
                <p className="text-base leading-[1.8] font-medium pt-4 opacity-100">
                  But enough to change the trajectory.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: REFRAME WITH IMAGE ===== */}
      <section className="relative py-20 lg:py-28 overflow-hidden" style={{ backgroundColor: '#1A3C34' }}>
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1579186163871-aca4725d6132?w=1400&q=80"
            alt="Woman looking forward"
            className="w-full h-full object-cover opacity-15"
            data-testid="reframe-bg-image"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(26,60,52,0.9) 0%, rgba(26,60,52,0.75) 100%)' }} />
        </div>
        <div className="max-w-2xl mx-auto px-6 text-center relative z-10">
          <p className="text-lg sm:text-xl text-white/50 leading-relaxed mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            This isn't about being ready for everything.
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight mb-10">
            This is about being ready for
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12">
            {[
              { word: 'support', Icon: HandHeart },
              { word: 'space', Icon: Compass },
              { word: 'a different way of moving', Icon: Wind }
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(192,108,84,0.2)' }}>
                  <item.Icon className="h-6 w-6" style={{ color: '#C06C54' }} />
                </div>
                <span className="text-2xl sm:text-3xl font-light italic" style={{ color: '#C06C54' }}>{item.word}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: WHO THIS IS FOR WITH IMAGE ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <div>
              <p className="text-[11px] uppercase tracking-[5px] mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                Is this you?
              </p>
              <h2 className="text-3xl sm:text-4xl font-light mb-10" style={{ color: '#1A3C34' }}>
                This next step is for you if you know&hellip;
              </h2>
              <div className="space-y-5">
                {[
                  "You're tired of carrying everything alone",
                  "You don't want to keep operating the same way",
                  "You're ready to be supported differently",
                  "You don't need more information \u2014 you need movement"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#C06C54' }}>
                      <Sparkles className="h-3.5 w-3.5 text-white" />
                    </div>
                    <p className="text-base leading-[1.8]" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/bu7vfsrq_8.png"
                  alt="Woman embracing freedom"
                  className="w-full h-[440px] object-cover"
                  data-testid="who-this-is-for-image"
                />
              </div>
              <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full opacity-15" style={{ backgroundColor: '#C06C54' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: FINAL TRUTH ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="space-y-4 mb-10">
            <p className="text-xl sm:text-2xl font-light" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              You don't need more time to think.
            </p>
            <p className="text-xl sm:text-2xl font-light" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              You already know more than you think you do.
            </p>
          </div>
          <div className="w-16 h-[1px] mx-auto mb-10" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-[11px] uppercase tracking-[4px] mb-5" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
            The only question is
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-12" style={{ color: '#1A3C34' }} data-testid="final-question">
            What are you going to do with it?
          </h2>
          {/* Final CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <a
              href="/inner-circle"
              className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-md text-sm tracking-wide"
              style={{ backgroundColor: '#C06C54' }}
              data-testid="final-cta-inner-circle"
            >
              Join Inner Circle <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="https://calendly.com/crystal-and-dr-nicole/one-on-one-with-crystal-and-dr-nicole"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-medium px-8 py-3.5 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide"
              style={{ backgroundColor: '#1A3C34' }}
              data-testid="final-cta-collective"
            >
              Let's Talk
            </a>
          </div>
        </div>
      </section>

      {/* ===== VOICES FROM THE SISTERHOOD ===== */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[5px] mb-5" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
              Sister Stories
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-normal italic mb-4" style={{ color: '#1A3C34' }} data-testid="testimonial-heading">
              Voices From the Sisterhood
            </h2>
            <p className="text-base max-w-md mx-auto" style={{ color: 'rgba(26,60,52,0.6)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              Real transformation stories from brilliant women who found their way home.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {[
              {
                name: 'Dr. Ursula Whitehead',
                title: 'Leadership Coach',
                quote: '"I am really grateful for the opportunity to work with Dr. Nicole and Crystal. They were just personal, inspiring and life-changing. If you\'re interested in making a change in..."',
                image: 'https://i.ontraport.com/260712.2334b2d50dcbe67a0fd0db7cf0ad7e13.PNG',
                videoUrl: 'https://vimeo.com/739435943'
              },
              {
                name: 'Tracy Higgs',
                title: 'Director, Office of Investigations, Nuclear Regulatory Commission',
                quote: '"The coaching I received transformed how I show up as a leader. I learned to lead with both strength and softness."',
                image: 'https://i.vimeocdn.com/video/1488692012-638ac0591ff0d31c43673736ff881dbfaffb1d81d765a9895170bae0f4516f4e-d',
                videoUrl: 'https://vimeo.com/739761167'
              },
              {
                name: 'CeWyon Chandler-Ward',
                title: 'Serial Entrepreneur | Angel Investor',
                quote: '"Sister Diamonds gave me the space to breathe, reflect, and strategize. The community is unlike anything I\'ve experienced."',
                image: 'https://i.vimeocdn.com/video/1489342970-59f6bd1640a0844f1cf844c0c52e8858da1c3718a81d1c7336311708bc3cabd5-d',
                videoUrl: 'https://vimeo.com/837081356'
              },
              {
                name: 'Arlene Henderson',
                title: 'Business Consultant, Executive Coach',
                quote: '"Working with Crystal and Dr. Nicole helped me unlock a level of clarity and confidence I didn\'t know was possible."',
                image: 'https://i.vimeocdn.com/video/1488709445-f620e9970488ed897bc868536f3e858af8a4498522a0d8a6d2717817580421ef-d',
                videoUrl: 'https://vimeo.com/739761994'
              }
            ].map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-500 flex flex-row"
                style={{ border: '1px solid rgba(26,60,52,0.06)' }}
                data-testid={`testimonial-card-${i}`}
              >
                <div className="relative w-[140px] sm:w-[180px] flex-shrink-0">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                  <a
                    href={t.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-4 left-4 w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                    style={{ backgroundColor: '#C06C54' }}
                    data-testid={`testimonial-play-${i}`}
                  >
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="none"><path d="M1 1L11 7L1 13V1Z" fill="white"/></svg>
                  </a>
                </div>
                <div className="p-5 sm:p-6 flex flex-col justify-between flex-1 min-w-0">
                  <div>
                    <svg width="28" height="22" viewBox="0 0 28 22" fill="none" className="mb-3" style={{ color: '#1A3C34' }}>
                      <path d="M0 22V13.2C0 10.6 0.5 8.3 1.5 6.3C2.6 4.2 4.3 2.4 6.6 1L8.8 3.8C7.3 4.7 6.1 5.8 5.3 7.2C4.5 8.5 4.1 10 4 11.5H8V22H0ZM16 22V13.2C16 10.6 16.5 8.3 17.5 6.3C18.6 4.2 20.3 2.4 22.6 1L24.8 3.8C23.3 4.7 22.1 5.8 21.3 7.2C20.5 8.5 20.1 10 20 11.5H24V22H16Z" fill="currentColor" opacity="0.15"/>
                    </svg>
                    <p className="text-sm leading-[1.7] mb-4" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>{t.quote}</p>
                  </div>
                  <div>
                    <p className="text-base font-semibold" style={{ color: '#1A3C34' }}>{t.name}</p>
                    <p className="text-xs mb-3" style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>{t.title}</p>
                    <a
                      href={t.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-70"
                      style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
                    >
                      <svg width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M0.5 0.5L9.5 6L0.5 11.5V0.5Z" fill="#C06C54"/></svg>
                      Watch Video
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLOSING SIGNATURE ===== */}
      <section className="py-14" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-[11px] uppercase tracking-[5px] text-white/40" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 400 }}>
            Crystal Khalil & Dr. Nicole LaBeach
          </p>
        </div>
      </section>
    </div>
  );
};

export default GoDeeper;
